import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const inDrawer = Boolean(this.closest('#cart-drawer'));

    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    minusButton.disabled = value <= min;
    this.classList.remove('quantity-selector--remove');

    if (inDrawer && minusButton.dataset.decreaseLabel) {
      minusButton.setAttribute('aria-label', minusButton.dataset.decreaseLabel);
    }
  }

  /**
   * In the cart drawer, the minimum quantity control removes the line.
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (this.classList.contains('quantity-selector--remove')) {
      event.preventDefault();
      const line = Number(this.refs.quantityInput?.dataset.cartLine);
      const cartItems = this.closest('cart-items-component');
      if (line && cartItems && 'onLineItemRemove' in cartItems) {
        /** @type {{ onLineItemRemove: (line: number) => void }} */ (cartItems).onLineItemRemove(line);
      }
      return;
    }

    super.decreaseQuantity(event);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
